import analytics from '../lib/analytics.mjs'
import { freshCsrfToken, redirectToSignIn } from '../lib/fresh_csrf_token.mjs'
import { reportError } from '../lib/error_reporter.mjs'

const CHECKOUT_ERROR_MESSAGE = 'We couldn’t start checkout. Please try again.'
// Another tab's checkout call may take up to the server's 20s Dodo deadline, so keep
// polling for ~24s before giving up (the server holds duplicates for 60s).
const PENDING_RETRY_DELAY = 2000
const PENDING_RETRY_ATTEMPTS = 12

const wait = delay => new Promise(resolve => setTimeout(resolve, delay))

// Expected, handled failures (declined checkout, network loss, error pages) only warn;
// anything else is a bug and is reported once.
const isExpectedCheckoutFailure = error => error instanceof TypeError || error instanceof SyntaxError

const reportUnexpectedCheckoutError = (error) => reportError('Checkout could not be started', error)

const showCheckoutError = (text = CHECKOUT_ERROR_MESSAGE) => {
  let container = document.getElementById('flash')
  if (!container) {
    container = document.createElement('div')
    container.id = 'flash'
    document.body.appendChild(container)
  }
  container.className = 'pointer-events-none fixed left-3 right-3 top-3 z-[1000] md:left-auto'

  const message = document.createElement('flash-message')
  message.dataset.duration = '5000'
  message.className = 'pointer-events-auto relative ml-auto flex w-fit max-w-full translate-y-0 items-center gap-2.5 overflow-hidden rounded-xl border border-base-300 bg-base-100 px-3 py-2.5 text-base-content shadow-lg transition duration-150 ease-out md:max-w-[28rem]'
  message.setAttribute('role', 'alert')
  message.setAttribute('aria-live', 'assertive')
  message.innerHTML = `
    <span class="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-error/10 text-error" aria-hidden="true">
      <svg class="h-5 w-5 stroke-current" fill="none" viewBox="0 0 24 24" stroke-width="2">
        <circle cx="12" cy="12" r="9"></circle>
        <path d="M12 8v5"></path>
        <path d="M12 16h.01"></path>
      </svg>
    </span>
    <span class="min-w-0 text-sm" data-checkout-error></span>
    <button type="button" data-flash-close class="flex h-7 w-7 flex-none items-center justify-center rounded-lg text-base-content/60 hover:bg-base-200 hover:text-base-content" aria-label="Close">×</button>
    <span class="absolute inset-x-0 bottom-0 h-0.5 overflow-hidden bg-base-300" role="progressbar" aria-label="Time remaining" aria-valuemin="0" aria-valuemax="5" aria-valuenow="5">
      <span data-flash-progress class="block h-full origin-left bg-error"></span>
    </span>
  `
  message.querySelector('[data-checkout-error]').textContent = text
  container.replaceChildren(message)
}

class PurchasePlan extends HTMLElement {
  connectedCallback () {
    this.setupEventListeners()
  }

  setupEventListeners () {
    this.purchaseHandler ||= this.handlePurchase.bind(this)
    const buttons = this.querySelectorAll('.purchase-button')
    buttons.forEach(button => {
      button.removeEventListener('click', this.purchaseHandler)
      button.addEventListener('click', this.purchaseHandler)
    })
  }

  async handlePurchase (event) {
    // Prevent the default button action
    event.preventDefault()

    // Get the button and prevent multiple clicks
    const button = event.currentTarget
    if (button.disabled) return

    // Disable the button temporarily
    button.disabled = true

    try {
      const planId = button.dataset.planId
      const discountCode = this.querySelector('.discount-code')?.value || ''

      // The page may have been restored by Back (e.g. returning from Dodo) with a token from an
      // older session state, so always ask for the current one first.
      const { token: csrfToken, signedIn } = await freshCsrfToken()
      if (!signedIn) {
        redirectToSignIn()
        return
      }

      const headers = { 'Content-Type': 'application/json', Accept: 'application/json' }
      if (csrfToken) headers['X-CSRF-Token'] = csrfToken
      const requestCheckout = async () => {
        const response = await fetch(`/credit_plans/${planId}/purchase`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            discount_code: discountCode
          })
        })

        // A lost session redirects to the sign-in page instead of answering with JSON.
        if (response.redirected && new URL(response.url).pathname === '/sign_in') {
          return { signInRequired: true }
        }

        return response.json()
      }

      // A pending response means another click/tab is already creating this checkout:
      // wait for it and reuse its checkout link instead of starting a second one.
      let data = await requestCheckout()
      for (let attempt = 0; data.pending && attempt < PENDING_RETRY_ATTEMPTS; attempt++) {
        await wait(PENDING_RETRY_DELAY)
        data = await requestCheckout()
      }

      if (data.signInRequired) {
        redirectToSignIn()
      } else if (data.success) {
        // Redirect to the payment link
        if (data.analytics) await analytics.trackBeforeNavigate('begin_checkout', data.analytics)
        window.location.assign(data.payment_link)
      } else {
        console.warn('Checkout could not be started:', data.error)
        // The server only returns its own copy (e.g. "plan unavailable"); textContent keeps it inert.
        showCheckoutError(typeof data.error === 'string' && data.error ? data.error : undefined)
      }
    } catch (error) {
      if (isExpectedCheckoutFailure(error)) console.warn('Checkout could not be started:', error)
      else reportUnexpectedCheckoutError(error)
      showCheckoutError()
    } finally {
      // Re-enable the button
      button.disabled = false
    }
  }

  disconnectedCallback () {
    // Clean up any event listeners if needed
    const buttons = this.querySelectorAll('.purchase-button')
    buttons.forEach(button => {
      button.removeEventListener('click', this.purchaseHandler)
    })
  }
}

export default PurchasePlan
